import * as React from "react";

import { Button } from "@/components/ui/button";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Class-based error boundary used to wrap each page (per requirements: every
 * page MUST have an error boundary). The fallback shows a user-friendly
 * message — never a stack trace.
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  private readonly handleReset = (): void => {
    this.setState({ error: null });
  };

  override render(): React.ReactNode {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div
          role="alert"
          className="mx-auto flex max-w-lg flex-col items-start gap-3 rounded-lg border bg-card p-6"
          data-testid="error-boundary"
        >
          <h2 className="text-lg font-semibold">Something went wrong</h2>
          <p className="text-sm text-muted-foreground">
            We hit an unexpected error rendering this page. Try again, and if it
            keeps happening reach out so we can investigate.
          </p>
          <Button onClick={this.handleReset} data-testid="error-boundary-reset">
            Try again
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}
